import type { JSX, TargetedKeyboardEvent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { clamp } from "@gss/common/clamp";
import { classNames } from "@gss/common/classNames";

interface Properties {
    current: number;
    size: number;
    limit: number;
    onChange: (page: number) => void;
    ariaLabel?: string;
    firstLabel?: string;
    lastLabel?: string;
    previousLabel?: string;
    nextLabel?: string;
    className?: string;
}

const span = 10;

export function Pagination({
    current,
    size,
    limit,
    onChange,
    ariaLabel,
    firstLabel,
    lastLabel,
    previousLabel,
    nextLabel,
    className,
}: Properties): JSX.Element {
    const [selected, setSelected] = useState(current);
    const numPages = Math.ceil(size / limit);
    const ref = useRef<HTMLUListElement>(null);

    useEffect(() => {
        if (selected > -1) {
            setSelected(current);
        }
    }, [current]);

    let min: number, max: number;
    // Base min/max on the closest edge.
    if (current < numPages - current) {
        min = Math.max(current - span / 2, 0);
        max = Math.min(min + span, numPages - 1);
    } else {
        max = Math.min(current + span / 2, numPages - 1);
        min = Math.max(max - span, 0);
    }

    const getSelectedLink = (
        selected: number,
    ): HTMLAnchorElement | undefined => {
        const ul = ref.current;
        if (ul == null || ul.children.length === 0) {
            return undefined;
        }
        switch (selected) {
            case -2:
                return getPageLink(ul.children[0]);
            case -1:
                return getPageLink(ul.children[1]);
            case -4:
                return getPageLink(ul.children[ul.children.length - 2]);
            case -3:
                return getPageLink(ul.children[ul.children.length - 1]);
            default: {
                const index = selected - min + 2;
                return getPageLink(ul.children[index]);
            }
        }
    };

    const update = (page: number) => {
        const clampedPage = clamp(page, 0, numPages - 1);
        if (current !== clampedPage) {
            onChange(clampedPage);
        }
    };

    const getFirstButton = () => {
        const label = firstLabel ?? "Första";
        return (
            <li className={getPreviousClass()}>
                <a
                    aria-label={label}
                    className="page-link"
                    tabIndex={selected === -2 ? 0 : -1}
                    onClick={() => update(0)}
                >
                    {label}
                </a>
            </li>
        );
    };

    const getPreviousButton = () => {
        const label = previousLabel ?? "Föregående";
        return (
            <li className={getPreviousClass()}>
                <a
                    aria-label={label}
                    className="page-link"
                    tabIndex={selected === -1 ? 0 : -1}
                    onClick={() => update(current - span)}
                >
                    <span aria-hidden="true">&laquo;</span>
                </a>
            </li>
        );
    };

    const getNumbers = () => {
        const res = [];
        for (let i = min; i <= max; ++i) {
            res.push(
                <li key={i} className={getNumberClass(i)}>
                    <a
                        aria-label={`Sida ${i}`}
                        className="page-link"
                        tabIndex={selected === i ? 0 : -1}
                        onClick={() => {
                            update(i);
                            setSelected(i);
                        }}
                    >
                        {i + 1}
                    </a>
                </li>,
            );
        }
        return res;
    };

    const getNextButton = () => {
        const label = nextLabel ?? "Nästa";
        return (
            <li className={getNextClass()}>
                <a
                    aria-label={label}
                    className="page-link"
                    tabIndex={selected === -4 ? 0 : -1}
                    onClick={() => update(current + span)}
                >
                    <span aria-hidden="true">&raquo;</span>
                </a>
            </li>
        );
    };

    const getLastButton = () => {
        const label = lastLabel ?? "Sista";
        return (
            <li className={getNextClass()}>
                <a
                    aria-label={label}
                    className="page-link"
                    tabIndex={selected === -3 ? 0 : -1}
                    onClick={() => update(numPages - 1)}
                >
                    {label} ({numPages})
                </a>
            </li>
        );
    };

    const getPreviousClass = () => {
        return classNames(
            "page-item",
            current === 0 ? "disabled" : "clickable",
        );
    };

    const getNextClass = () => {
        return classNames(
            "page-item",
            current === numPages - 1 ? "disabled" : "clickable",
        );
    };

    const getNumberClass = (index: number) => {
        return classNames(
            "page-item",
            index === current ? "active" : "clickable",
        );
    };

    const navigate = (index: number) => {
        setSelected(index);
        getSelectedLink(index)?.focus();
    };

    const onKeyDown = (e: TargetedKeyboardEvent<Element>) => {
        switch (e.key) {
            case "ArrowLeft":
                if (selected === -2) {
                    // Do nothing
                } else if (selected === -4) {
                    navigate(max);
                } else if (selected > -4) {
                    navigate(selected - 1);
                }
                break;
            case "ArrowRight":
                if (selected === -3) {
                    // Do nothing
                } else if (selected === max) {
                    navigate(-4);
                } else if (selected > -5) {
                    navigate(selected + 1);
                }
                break;
            case "Home":
                navigate(-2);
                break;
            case "End":
                navigate(-3);
                break;
            case "Enter":
            case " ":
                getSelectedLink(selected)?.click();
                break;
            default:
                return;
        }
        e.preventDefault();
    };

    return (
        <nav role="navigation" aria-label={ariaLabel ?? "Sidnavigation"}>
            <ul
                className={classNames("pagination", className)}
                ref={ref}
                onKeyDown={onKeyDown}
            >
                {getFirstButton()}
                {getPreviousButton()}
                {getNumbers()}
                {getNextButton()}
                {getLastButton()}
            </ul>
        </nav>
    );
}

function getPageLink(node: Element): HTMLAnchorElement | undefined {
    return node.firstElementChild instanceof HTMLAnchorElement
        ? node.firstElementChild
        : undefined;
}
